<template>

    <validation-observer ref="validationObserver" v-slot="{ invalid, errors }"
                         v-if="availableValidators.length > 0">


        <title-card class="mb-2"
                    :title="validator.name"
                    v-for="validator in availableValidators"
                    no-padding
                    :key="validator.type">

            <template v-slot:buttons>

                <div class="d-flex">
                    <v-chip v-if="validator.expressionPreview"
                            class="mr-3 align-self-center"
                            label
                            small
                            color="grey darken-2">
                        {{ validator.expressionPreview }}
                    </v-chip>

                    <v-chip v-if="validator.enabled"
                            small
                            class="text-center mr-3 align-self-center"
                            color="success">
                        ENABLED
                    </v-chip>

                    <div class="align-self-center">
                        <v-switch v-model="validator.enabled"
                                  class="mr-5"
                                  hide-details
                                  @change="emit"
                        ></v-switch>
                    </div>

                </div>

            </template>

            <!-- EXAMPLE VALUE -->

            <span v-if="false">
                {{ validator.name }}
            </span>

            <!-- MINIMUM LENGTH VALIDATOR -->
            <div v-if="validator.type==='min'" class="config-area" v-show="validator.enabled">

                <validation-provider
                    name="minimum length"
                    rules="required_if:minToggle,true|integer|min_value:1|max_value:2000"
                    v-slot="{ errors }">

                    <v-text-field v-model="validator.param1"
                                  clearable
                                  placeholder="Enter minimum length"
                                  hint="Minimum length in characters"
                                  style="width: 200px"
                                  persistent-hint
                                  :error-messages="errors"
                                  @input="validatorsChanged(validator)"
                                  @click.clear="validatorsChanged(validator)"
                    ></v-text-field>

                </validation-provider>
            </div>


            <!-- MAXIMUM LENGTH VALIDATOR -->
            <div v-if="validator.type==='max'" v-show="validator.enabled" class="config-area">

                <validation-provider
                    name="maximum length"
                    rules="required_if:maxToggle,true|integer|min_value:1|max_value:2000"
                    v-slot="{ errors }">

                    <v-text-field v-model="validator.param1"
                                  clearable
                                  float
                                  style="width: 300px"
                                  persistent-hint
                                  hint="Maximum length in characters"
                                  @input="validatorsChanged(validator)"
                                  :error-messages="errors"
                                  placeholder="Enter maximum length">
                    </v-text-field>

                </validation-provider>
            </div>

            <!-- MINIMUM VALUE VALIDATOR -->
            <div v-if="validator.type==='min_value'" v-show="validator.enabled" class="config-area">

                <validation-provider
                    name="minimum value"
                    rules="required_if:minValueToggle,true|numeric"
                    v-slot="{ errors }">

                    <v-text-field v-model="validator.param1"
                                  clearable
                                  style="width: 300px"
                                  :error-messages="errors"
                                  @input="validatorsChanged(validator)"
                                  placeholder="Enter minimum value"
                                  hint="Minimum value"
                                  persistent-hint
                    ></v-text-field>

                </validation-provider>

            </div>

            <!-- MAXIMUM VALUE VALIDATOR -->
            <div v-if="validator.type==='max_value'" v-show="validator.enabled" class="config-area">

                <validation-provider
                    name="maximum value"
                    rules="required_if:maxValueToggle,true|numeric"
                    v-slot="{ errors }">

                    <v-text-field v-model="validator.param1"
                                  clearable
                                  :error-messages="errors"
                                  style="width: 300px"
                                  @input="validatorsChanged(validator)"
                                  hint="Max # of checks required"
                                  persistent-hint
                                  placeholder="Enter man # of checks required">
                    </v-text-field>

                </validation-provider>
            </div>

            <!-- MINIMUM DATE VALIDATOR -->
            <div v-if="validator.type==='min_date'" v-show="validator.enabled" class="config-area">

                <validation-provider
                    name="minimum date"
                    rules="required_if:minDateToggle,true"
                    v-slot="{ errors }">

                    <date-picker-field v-model="validator.param1"
                                       @input="validatorsChanged(validator)"
                                       clearable
                                       label="Minimum date"
                                       :error-messages="errors"
                    ></date-picker-field>

                </validation-provider>
            </div>

            <!-- MAXIMUM DATE VALIDATOR -->
            <div v-if="validator.type==='max_date'" v-show="validator.enabled" class="config-area">
                <validation-provider
                    name="maximum date"
                    rules="required_if:maxDateToggle,true"
                    v-slot="{ errors }">

                    <date-picker-field v-model="validator.param1"
                                       clearable
                                       @input="validatorsChanged(validator)"
                                       label="Maximum date"
                                       :error-messages="errors"
                    ></date-picker-field>

                </validation-provider>
            </div>


            <!-- MINIMUM CHECKS VALIDATOR -->
            <div v-if="validator.type==='min_checks'" v-show="validator.enabled" class="config-area">

                <validation-provider
                    name="minimum checks"
                    rules="required_if:minChecksToggle,true|integer|min_value:1"
                    v-slot="{ errors }">

                    <v-text-field v-model="validator.param1"
                                  clearable
                                  style="width: 300px"
                                  :error-messages="errors"
                                  @input="validatorsChanged(validator)"
                                  hint="Min # of checks required"
                                  persistent-hint
                                  placeholder="Enter min # of checks required">

                    </v-text-field>

                </validation-provider>

            </div>

            <!-- MAXIMUM CHECKS VALIDATOR -->
            <div v-if="validator.type==='max_checks'" v-show="validator.enabled" class="config-area">
                <validation-provider
                    name="maximum checks"
                    rules="required_if:maxChecksToggle,true|integer|min_value:1"
                    v-slot="{ errors }">

                    <v-text-field v-model="validator.param1"
                                  clearable
                                  style="width: 300px"
                                  :error-messages="errors"
                                  @input="validatorsChanged(validator)"
                                  hint="Maximum number of checked items"
                                  persistent-hint
                                  placeholder="Enter maximum value">

                    </v-text-field>

                </validation-provider>
            </div>

            <!-- BETWEEN -->
            <div v-if="validator.type==='between'" v-show="validator.enabled" class="config-area">

                <div class="d-flex">
                    <validation-provider
                        name="from"
                        rules="required_if:betweenToggle,true|numeric|is_smaller_than:to"
                        mode="lazy"
                        v-slot="{ errors }">

                        <v-text-field v-model="validator.param1"
                                      clearable
                                      @input="validatorsChanged(validator)"
                                      persistent-hint
                                      style="width: 300px"
                                      :error-messages="errors"
                                      hint="Minimum value allowed"
                                      placeholder="Enter minimum value">
                        </v-text-field>
                    </validation-provider>

                    <validation-provider
                        name="to"
                        rules="required_if:betweenToggle,true|numeric|is_greater_than:from"
                        mode="lazy"
                        v-slot="{ errors }">

                        <v-text-field v-model="validator.param2"
                                      clearable
                                      class="ml-10"
                                      @input="validatorsChanged(validator)"
                                      persistent-hint
                                      style="width: 300px"
                                      :error-messages="errors"
                                      hint="Maximum allowed value"
                                      placeholder="Enter maximum value">

                        </v-text-field>
                    </validation-provider>
                </div>

            </div>

            <!-- REGULAR EXPRESSION VALIDATOR -->
            <v-scale-transition>
                <div v-if="validator.type==='regex'" v-show="validator.enabled" class="config-area">

                    <div class="d-flex">

                        <validation-provider
                            name="Regexp"
                            rules="required_if:regexToggle,true"
                            v-slot="{ errors }">

                            <v-text-field v-model="validator.param1"
                                          clearable
                                          style="width: 600px"
                                          class="align-self-center"
                                          placeholder="Enter regular expression"
                                          @input="regexpUpdated($event, validator)"
                                          hint="Regular expression"
                                          :error-messages="regexpError ? [regexpError] : errors"
                                          persistent-hint
                            ></v-text-field>
                        </validation-provider>

                        <div class="align-self-center ml-5">
                            <v-btn text small @click="openRegexpDotCom">
                                <v-icon left>mdi-help-circle-outline</v-icon>
                                Learn at Regexp.com
                            </v-btn>
                        </div>

                    </div>

                    <div class="d-flex" style="width: 600px">
                        <v-text-field v-model="validator.param2"
                                      clearable
                                      style="width: 200px;"
                                      class="align-self-center"
                                      placeholder="Enter test string"
                                      @input="regexpTestStringUpdated($event,validator)"
                                      hint="Test string"
                                      persistent-hint
                        ></v-text-field>

                        <div class="align-self-center ml-10"
                             style="width: 150px"
                             v-if="validator.param1 && validator.param1.length > 0 && regexpTestString && regexpTestString.length > 0">
                            <v-chip color="success" v-if="regexpResult">Matches {{ regexpMatch }}</v-chip>
                            <v-chip color="error" v-if="!regexpResult">No match</v-chip>
                        </div>
                    </div>
                </div>
            </v-scale-transition>

        </title-card>

    </validation-observer>

</template>

<script>

import {extend} from 'vee-validate/dist/vee-validate.full';
import DatePickerField from "../../../components/UI/DatePickerField";
import TitleCard from "../../../components/UI/TitleCard";
import Json from 'vue-json-pretty';

export default {

    name: "Validation",

    components: {
        TitleCard,
        DatePickerField,
        Json
    },

    props: {
        variable: {
            type: Object | null,
            required: true
        }
    },

    computed: {

        availableValidators() {

            if (this.variable.dynamic_profiling_validators) {
                this.variable.dynamic_profiling_validators.forEach(validator => {
                    let found = this.validators.find(v => v.type === validator.type);
                    if (found) {
                        found.enabled = true;
                        if (validator.param1 && !found.param1) found.param1 = validator.param1;
                        if (validator.param2 && !found.param2) found.param2 = validator.param2;
                    }
                })
            }

            return this.validators.filter(validation => validation.dataVariableTypes.includes(this.variable.type));
        },

        enabledValidatorCount() {
            return this.validators.filter(v => v.enabled === true).length;
        }
    },

    data() {
        return {
            regexp: null,
            regexpTestString: null,
            regexpValid: false,
            regexpResult: false,
            regexpMatch: null,
            regexpError: null,
            validators: [
                {
                    name: 'Required',
                    type: 'required',
                    dataVariableTypes: ['text', 'keyword', 'number', 'email', 'radio', 'checkbox', 'date', 'locale', 'phone'],
                    previewExpression: null,
                    enabled: false,
                    paramCount: 0,
                },
                {
                    name: 'Minimum length',
                    type: 'min',
                    expression: '>= [param1]',
                    param1: null,
                    dataVariableTypes: ['text', 'keyword', 'email'],
                    previewExpression: null,
                    enabled: false,
                    paramCount: 1,
                },
                {
                    name: 'Maximum length',
                    type: 'max',
                    expression: '<= [param1]',
                    param1: null,
                    dataVariableTypes: ['text', 'keyword', 'email'],
                    previewExpression: null,
                    enabled: false,
                    paramCount: 1,
                },
                {
                    name: 'Min checks',
                    type: 'min_checks',
                    expression: 'min # of checks [param1]',
                    param1: null,
                    dataVariableTypes: ['checkbox'],
                    previewExpression: null,
                    enabled: false,
                    paramCount: 1,
                },
                {
                    name: 'Max checks',
                    type: 'max_checks',
                    expression: 'max # of checks [param1]',
                    param1: null,
                    dataVariableTypes: ['checkbox'],
                    previewExpression: null,
                    enabled: false,
                    paramCount: 1,
                },
                {
                    name: 'Alphabetic characters only',
                    type: 'alpha',
                    dataVariableTypes: ['text', 'keyword'],
                    previewExpression: null,
                    enabled: false,
                    paramCount: 0,
                },
                {
                    name: 'Alphabetic or numeric characters only',
                    type: 'alpha_num',
                    dataVariableTypes: ['text', 'keyword'],
                    previewExpression: null,
                    enabled: false,
                    paramCount: 0,
                },
                {
                    name: 'Numeric characters only',
                    type: 'numeric',
                    dataVariableTypes: ['text', 'keyword'],
                    previewExpression: null,
                    enabled: false,
                    paramCount: 0,
                },
                {
                    name: 'Integer',
                    type: 'integer',
                    dataVariableTypes: ['number', 'keyword'],
                    previewExpression: null,
                    enabled: false,
                    paramCount: 0,
                },
                {
                    name: 'Minimum value',
                    type: 'min_value',
                    expression: '>= [param1]',
                    param1: null,
                    dataVariableTypes: ['number'],
                    enabled: false,
                    paramCount: 1,
                },
                {
                    name: 'Maximum value',
                    type: 'max_value',
                    expression: '<= [param1]',
                    param1: null,
                    dataVariableTypes: ['number'],
                    previewExpression: null,
                    enabled: false,
                    paramCount: 1,
                },
                {
                    name: 'Minimum date',
                    type: 'min_date',
                    expression: '>= [param1]',
                    param1: null,
                    dataVariableTypes: ['date'],
                    enabled: false,
                    paramCount: 1,
                },
                {
                    name: 'Maximum date',
                    type: 'max_date',
                    expression: '=< [param1]',
                    param1: null,
                    dataVariableTypes: ['date'],
                    enabled: false,
                    paramCount: 1,
                },
                {
                    name: 'Value between',
                    type: 'between',
                    expression: '[param1] <==> [param2]',
                    param1: null,
                    param2: null,
                    dataVariableTypes: ['number'],
                    previewExpression: null,
                    enabled: false,
                    paramCount: 2,
                },
                {
                    name: 'Regular expression validation (advanced)',
                    type: 'regex',
                    expression: '[param1]',
                    param1: null,
                    dataVariableTypes: ['text', 'keyword', 'number', 'email'],
                    previewExpression: null,
                    enabled: false,
                    paramCount: 1,
                },
            ],
        }
    },

    methods: {
        validationRegexpChanged() {

            let validator = this.validators.find(validator => validator.type === 'regex');

            if (!validator.param1 || !validator.param2 || validator.param2.length === 0 || validator.param2.length === 0) {

                this.$nextTick(() => {
                    validator.enabled = false;
                });
                if (validator.enabled) {
                    Bus.$emit('flash-message', {
                        type: 'error',
                        message: 'Please enter valid regular expression and a valid test string'
                    })
                }
            } else {

                this.emit();
                this.findMatches();

                if (!this.regexpResult) {

                    if (validator.enabled) {

                        Bus.$emit('flash-message', {
                            message: 'Please enter a valid test string to confirm entered regular expression works!'
                        })
                    }
                }
            }
        },

        regexpUpdated(value, validator) {
            this.regexp = value;
            this.validatorsChanged(validator);
            this.findMatches();
        },

        regexpTestStringUpdated(value, validator) {

            // Hack as value is not yet stored in the model
            this.regexpTestString = value;
            this.validatorsChanged(validator);
            this.findMatches();
        },

        findMatches() {

            this.regexpError = null;

            try {
                new RegExp(this.regexp);
                this.regexpValid = true;
            } catch (e) {
                this.regexpValid = false;
                this.regexpError = e.name + ': ' + e.message;
            }

            if (this.regexpValid) {
                const regexp = RegExp(this.regexp);
                this.regexpResult = regexp.test(this.regexpTestString);
                const match = regexp.exec(this.regexpTestString);

                if (match) {
                    this.regexpMatch = match[0];
                }
            }
        },

        validatorsChanged(validator) {
            this.emit();
        },

        openRegexpDotCom() {
            this.sendToUrl('https://regexr.com/', true);
        },

        emit() {

            this.$refs.validationObserver.validate()
                .then(isValid => {

                    const validators = this.validators.filter(validator => validator.enabled)
                        .map(validator => {
                            let data = {};
                            data.type = validator.type;
                            if (validator.param1) data.param1 = validator.param1;
                            if (validator.param2) data.param2 = validator.param2;
                            return data;
                        });

                    if (validators.length > 0) {
                        this.$emit('change', validators);
                    } else {
                        this.$emit('change', null);
                    }
                });
        }
    },

    created() {
        extend('is_smaller_than', {
            params: [{name: 'other', isTarget: true}],
            validate: (value, {other}) => value < other,
            message: 'Value must be smaller than {target}',
            lazy: true
        });

        extend('is_greater_than', {
            params: [{name: 'other', isTarget: true}],
            validate: (value, {other}) => value > other,
            message: 'Value must be greater than {target}',
            lazy: true
        });
    }
}
</script>

<style scoped>
.config-area {
    padding: 0.2em 1.5em 1.5em 1.5em;
}
</style>
